import { MutableRefObject, useCallback } from 'react';

import EditableItemDatePicker from 'dashboard/components/FormControlPilledInput/EditableItemDatePicker';
import EditableItemDateTimePicker from 'dashboard/components/FormControlPilledInput/EditableItemDateTimePicker';
import EditableItemDateTimeRangePicker from 'dashboard/components/FormControlPilledInput/EditableItemDateTimeRangePicker';
import EditableItemInput from 'dashboard/components/FormControlPilledInput/EditableItemInput';
import EditableItemNumberPicker from 'dashboard/components/FormControlPilledInput/EditableItemNumberPicker';
import EditableItemSelect from 'dashboard/components/FormControlPilledInput/EditableItemSelect';
import EditableItemTimeFramePicker from 'dashboard/components/FormControlPilledInput/EditableItemTimeFramePicker';
import EditableItemVariableInput from 'dashboard/components/FormControlPilledInput/EditableItemVariableInput';
import {
  Control,
  ControlConfig,
  DateTimeValueRange,
  Value,
} from 'dashboard/components/FormControlPilledInput/types';
import {
  DateTimeValue,
  DateValue,
  NumberValue,
  RangeValue,
  TextValue,
  ValueType,
} from 'dashboard/models/graph-query';
import { assertNever } from 'lib/utils/types';

interface EditableItemProps {
  controlConfig: ControlConfig;
  deleteItem?: () => void;
  deleteLastItem?: () => void;
  itemKey?: string;
  isLast: boolean;
  inputRef: MutableRefObject<HTMLInputElement> | null | undefined;
  onValueChange: (value: Value | [DateTimeValue, DateTimeValue]) => void;
  placeholder?: string | null;
  value?: Value | [DateTimeValue, DateTimeValue];
  isCompact?: boolean;
  isVariable?: boolean;
}

// EditableItem helps convert amongst different form controls that could be used
// to edit pill values.
const EditableItem = ({
  controlConfig,
  deleteItem,
  deleteLastItem,
  inputRef,
  isLast,
  itemKey,
  onValueChange,
  placeholder,
  value: initialValue,
  isVariable = false,
  // Remove this once GraphV2 has ramped 100%
  isCompact = false,
}: EditableItemProps) => {
  const handleInputValueChange = useCallback(
    (value: Value | [DateTimeValue, DateTimeValue]) => {
      onValueChange(value);
    },
    [onValueChange],
  );

  switch (controlConfig.type) {
    case Control.Input: {
      if (!initialValue) {
        initialValue = { type: ValueType.Text, 'raw-value': '', value: '' };
      }

      const InputComponent =
        isVariable && !('errored' in initialValue && initialValue.errored)
          ? EditableItemVariableInput
          : EditableItemInput;
      return (
        <InputComponent
          controlConfig={controlConfig}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          deleteItem={deleteItem}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          deleteLastItem={deleteLastItem}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          inputRef={inputRef}
          isLast={isLast}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          itemKey={itemKey}
          value={initialValue as TextValue}
          onValueChange={handleInputValueChange}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          placeholder={placeholder}
          isCompact={isCompact}
        />
      );
    }
    case Control.Select:
      return (
        <EditableItemSelect
          controlConfig={controlConfig}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          deleteItem={deleteItem}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          deleteLastItem={deleteLastItem}
          isLast={isLast}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          inputRef={inputRef}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          itemKey={itemKey}
          onValueChange={handleInputValueChange}
          placeholder={placeholder}
          value={initialValue as TextValue}
          isCompact={isCompact}
        />
      );
    case Control.NumberPicker:
      if (!initialValue) {
        initialValue = {
          type: ValueType.Number,
          'raw-value': '',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          value: null,
        };
      }

      return (
        <EditableItemNumberPicker
          controlConfig={controlConfig}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          deleteItem={deleteItem}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          deleteLastItem={deleteLastItem}
          isLast={isLast}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          inputRef={inputRef}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          itemKey={itemKey}
          onValueChange={handleInputValueChange}
          value={initialValue as NumberValue}
        />
      );
    case Control.DatePicker:
      if (!initialValue) {
        initialValue = {
          type: ValueType.Date,
          'raw-value': '',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          value: null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          moment: null,
        };
      }

      return (
        <EditableItemDatePicker
          controlConfig={controlConfig}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          deleteItem={deleteItem}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          deleteLastItem={deleteLastItem}
          isLast={isLast}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          itemKey={itemKey}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          inputRef={inputRef}
          onValueChange={handleInputValueChange}
          value={initialValue as DateValue}
          isCompact={isCompact}
        />
      );
    case Control.DateTimePicker:
      if (!initialValue) {
        initialValue = {
          type: ValueType.DateTime,
          'raw-value': '',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          value: null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          moment: null,
        };
      }

      return (
        <EditableItemDateTimePicker
          controlConfig={controlConfig}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          deleteItem={deleteItem}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          deleteLastItem={deleteLastItem}
          isLast={isLast}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          itemKey={itemKey}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          inputRef={inputRef}
          onValueChange={handleInputValueChange}
          value={initialValue as DateTimeValue}
          isCompact={isCompact}
        />
      );
    case Control.DateTimeRangePicker:
      if (!initialValue) {
        initialValue = [
          {
            type: ValueType.DateTime,
            'raw-value': '',
          },
          {
            type: ValueType.DateTime,
            'raw-value': '',
          },
        ] as [DateTimeValue, DateTimeValue];
      }

      return (
        <EditableItemDateTimeRangePicker
          controlConfig={controlConfig}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          deleteItem={deleteItem}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          deleteLastItem={deleteLastItem}
          isLast={isLast}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          itemKey={itemKey}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          inputRef={inputRef}
          onValueChange={handleInputValueChange}
          value={initialValue as DateTimeValueRange}
          isCompact={isCompact}
        />
      );
    case Control.TimeFramePicker:
      if (!initialValue) {
        initialValue = {
          type: ValueType.Range,
          'range-value-type': controlConfig.rangeValueType,
          'raw-value': '',
          value: '',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'parsed-relative-time': undefined,
          range: null,
        };
      }
      return (
        <EditableItemTimeFramePicker
          controlConfig={controlConfig}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          deleteItem={deleteItem}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          deleteLastItem={deleteLastItem}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          inputRef={inputRef}
          isLast={isLast}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          itemKey={itemKey}
          value={initialValue as RangeValue}
          onValueChange={handleInputValueChange}
          isCompact={isCompact}
        />
      );
    case Control.None:
      return <></>;
    default: {
      return assertNever(controlConfig);
    }
  }
};

export default EditableItem;
